import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Button, Modal, Form, Input, Switch, InputNumber, message, Card, Tooltip, Select, Tabs } from 'antd';
import api from "../adapter/base";
import { DeleteOutlined, EditOutlined, LinkOutlined } from "@ant-design/icons";
import { ConstantsSkeleton } from "./loadingSkeletons";

const ConstantsManager = () => {
    const [form] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingConstant, setEditingConstant] = useState(null);
    const [constantType, setConstantType] = useState('WALLET_REWARD_PRICE_REGULATOR');
    const queryClient = useQueryClient();

    const { data: constantsData, isLoading } = useQuery({
        queryKey: ['constants'],
        queryFn: async () => {
            const { data } = await api.get('constants');
            return data;
        }
    });

    const { mutate: saveConstant } = useMutation({
        mutationFn: async (values) => {
            const formData = new FormData();

            if (values.type === 'YOUTUBE_LINK_CONSTANT_ONE') {
                formData.append('type', values.type);
                formData.append('description', values.youtube_url);
                formData.append('check_order_price', '1');
                formData.append('buyer_wallet_reward_price', '1');
                formData.append('referrer_wallet_reward_price', '1');
                formData.append('is_active', values.is_active ? '1' : '0');
            } else {
                Object.entries(values).forEach(([key, value]) => {
                    formData.append(key, value);
                });
                formData.append('check_order_price', String(values.check_order_price));
                formData.append('buyer_wallet_reward_price', String(values.buyer_wallet_reward_price));
                formData.append('referrer_wallet_reward_price', String(values.referrer_wallet_reward_price));
            }

            if (editingConstant) {
                formData.append('_method', 'put');
                const { data } = await api.post(`constants/${editingConstant.id}`, formData);
                return data;
            } else {
                const { data } = await api.post('constants', formData);
                return data;
            }
        },
        onSuccess: () => {
            message.success(editingConstant ? 'Constant updated successfully' : 'Constant created successfully');
            queryClient.invalidateQueries(['constants']);
            handleCancel();
        },
        onError: (error) => {
            message.error(error.response?.data?.message || 'An error occurred');
        }
    });

    const { mutate: deleteConstant } = useMutation({
        mutationFn: async (id) => {
            await api.delete(`constants/${id}`);
        },
        onSuccess: () => {
            message.success('Constant deleted successfully');
            queryClient.invalidateQueries(['constants']);
        },
        onError: () => {
            message.error('Failed to delete constant');
        }
    });

    const handleDelete = (id) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this constant?',
            content: 'This action cannot be undone',
            okText: 'Yes, delete it',
            okType: 'danger',
            cancelText: 'Cancel',
            className: '[&_.ant-modal-content]:!rounded-2xl',
            okButtonProps: {
                className: '!bg-red-500 !text-white !rounded-lg hover:!bg-red-600'
            },
            onOk: () => deleteConstant(id),
        });
    };

    const handleEdit = (record) => {
        setEditingConstant(record);
        setConstantType(record.type);
        form.setFieldsValue({
            ...record,
            is_active: record.is_active === 1,
            ...(record.type === 'YOUTUBE_LINK_CONSTANT_ONE' && {
                youtube_url: record.description,
                check_order_price: 0,
                buyer_wallet_reward_price: 0,
                referrer_wallet_reward_price: 0
            })
        });
        setIsModalOpen(true);
    };

    const handleCreate = () => {
        setEditingConstant(null);
        setConstantType('WALLET_REWARD_PRICE_REGULATOR');
        form.resetFields();
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setEditingConstant(null);
        setConstantType('WALLET_REWARD_PRICE_REGULATOR');
    };

    const onFinish = (values) => {
        const payload = {
            ...values,
            type: constantType,
            is_active: values.is_active ? '1' : '0',
            ...(constantType === 'YOUTUBE_LINK_CONSTANT_ONE' && {
                youtube_url: values.youtube_url,
                check_order_price: 0,
                buyer_wallet_reward_price: 0,
                referrer_wallet_reward_price: 0
            })
        };

        saveConstant(payload);
    };

    return (
        <div className="py-10 px-32 bg-gray-50 min-h-screen">
            <div className="mb-6 flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-800">Constants Management</h1>
                <Button
                    onClick={handleCreate}
                    className="!bg-yellow-500 !text-white !rounded-lg hover:!bg-yellow-600 !flex items-center gap-2"
                >
                    <span>+</span> Create New Constant
                </Button>
            </div>

            {isLoading ? (
                <ConstantsSkeleton />
            ) : (
                <Tabs defaultActiveKey="1" className="!bg-white !rounded-xl !p-4 !shadow-lg">
                    <Tabs.TabPane tab="Wallet Rewards" key="1">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {constantsData?.data?.filter(constant => constant.type === "WALLET_REWARD_PRICE_REGULATOR").map(constant => (
                                <Card
                                    key={constant.id}
                                    className="!rounded-xl !shadow-lg hover:!shadow-xl transition-all"
                                >
                                    <div className="flex flex-col gap-3">
                                        <div className="flex justify-between items-start">
                                            <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">
                                                {constant.type}
                                            </span>
                                            <Switch
                                                checked={constant.is_active === 1}
                                                disabled
                                                className={`${constant.is_active ? '!bg-yellow-500' : '!bg-gray-300'}`}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Check Order Price:</span>
                                                <span className="text-lg font-semibold text-yellow-600">
                                                    {constant.check_order_price} br
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Buyer Reward:</span>
                                                <span className="text-lg font-semibold text-green-600">
                                                    {constant.buyer_wallet_reward_price} br
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Referrer Reward:</span>
                                                <span className="text-lg font-semibold text-blue-600">
                                                    {constant.referrer_wallet_reward_price} br
                                                </span>
                                            </div>
                                        </div>

                                        {constant.description && (
                                            <p className="text-gray-600 text-sm mt-2">
                                                {constant?.description ?? ""}
                                            </p>
                                        )}

                                        <div className="flex gap-2 mt-4">
                                            <Button
                                                onClick={() => handleEdit(constant)}
                                                icon={<EditOutlined/>}
                                                className="!bg-yellow-500 !text-white !rounded-lg hover:!bg-yellow-600 !flex-1"
                                            >
                                                Edit
                                            </Button>
                                            <Tooltip title="Delete constant">
                                                <Button
                                                    danger
                                                    icon={<DeleteOutlined/>}
                                                    onClick={() => handleDelete(constant.id)}
                                                    className="!rounded-lg !flex items-center justify-center"
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </Tabs.TabPane>

                    <Tabs.TabPane tab="YouTube Constants" key="2">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {constantsData?.data?.filter(constant => constant.type === "YOUTUBE_LINK_CONSTANT_ONE").map(constant => (
                                <Card
                                    key={constant.id}
                                    className="!rounded-xl !shadow-lg hover:!shadow-xl transition-all"
                                >
                                    <div className="flex flex-col gap-3">
                                        <div className="flex justify-between items-start">
                                            <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
                                                YouTube Link
                                            </span>
                                            {/*<Switch*/}
                                            {/*    checked={constant.is_active === 1}*/}
                                            {/*    disabled*/}
                                            {/*    className={`${constant.is_active ? '!bg-red-500' : '!bg-gray-300'}`}*/}
                                            {/*/>*/}
                                        </div>

                                        <div className="space-y-2">
                                            <a
                                                href={constant.description}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 break-words"
                                            >
                                                <LinkOutlined /> {constant.description}
                                            </a>
                                        </div>

                                        <div className="flex gap-2 mt-4">
                                            <Button
                                                onClick={() => handleEdit(constant)}
                                                icon={<EditOutlined/>}
                                                className="!bg-red-500 !text-white !rounded-lg hover:!bg-red-600 !flex-1"
                                            >
                                                Edit
                                            </Button>
                                            <Tooltip title="Delete constant">
                                                <Button
                                                    danger
                                                    icon={<DeleteOutlined/>}
                                                    onClick={() => handleDelete(constant.id)}
                                                    className="!rounded-lg !flex items-center justify-center"
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </Tabs.TabPane>
                </Tabs>
            )}

            <Modal
                title={editingConstant ? 'Edit Configuration' : 'New Configuration'}
                open={isModalOpen}
                onCancel={handleCancel}
                footer={null}
                centered
                destroyOnClose
                className="[&_.ant-modal-content]:!rounded-2xl [&_.ant-modal-header]:!border-0 [&_.ant-modal-title]:!text-xl [&_.ant-modal-title]:!font-bold"
            >
                <Form
                    form={form}
                    onFinish={onFinish}
                    layout="vertical"
                    initialValues={{
                        is_active: true,
                        type: 'WALLET_REWARD_PRICE_REGULATOR'
                    }}
                >
                    {!editingConstant && (
                        <Form.Item
                            label="Constant Type"
                            name="type"
                            rules={[{ required: true }]}
                            className="!mb-4"
                        >
                            <Select
                                onChange={(value) => setConstantType(value)}
                                className="!rounded-lg"
                            >
                                <Select.Option value="WALLET_REWARD_PRICE_REGULATOR">Wallet Reward</Select.Option>
                                <Select.Option value="YOUTUBE_LINK_CONSTANT_ONE">YouTube Link</Select.Option>
                            </Select>
                        </Form.Item>
                    )}

                    {constantType === 'YOUTUBE_LINK_CONSTANT_ONE' ? (
                        <Form.Item
                            label="YouTube URL"
                            name="youtube_url"
                            rules={[{ required: true, message: 'Please input YouTube URL!' }]}
                            className="!mb-4"
                        >
                            <Input
                                placeholder="https://youtube.com/..."
                                className="!rounded-lg"
                            />
                        </Form.Item>
                    ) : (
                        <>
                            <Form.Item
                                label="Check Order Price (br)"
                                name="check_order_price"
                                rules={[{ required: true, message: 'Required field' }]}
                                className="!mb-4"
                            >
                                <InputNumber
                                    className="w-full !rounded-lg"
                                    min={1}
                                    addonAfter="br"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Buyer Reward (br)"
                                name="buyer_wallet_reward_price"
                                rules={[{ required: true, message: 'Required field' }]}
                                className="!mb-4"
                            >
                                <InputNumber
                                    className="w-full !rounded-lg"
                                    min={1}
                                    addonAfter="br"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Referrer Reward (br)"
                                name="referrer_wallet_reward_price"
                                rules={[{ required: true, message: 'Required field' }]}
                                className="!mb-4"
                            >
                                <InputNumber
                                    className="w-full !rounded-lg"
                                    min={1}
                                    addonAfter="br"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Active Status"
                                name="is_active"
                                valuePropName="checked"
                                className="!mb-4"
                            >
                                <Switch
                                    checkedChildren="ACTIVE"
                                    unCheckedChildren="INACTIVE"
                                    className="!bg-gray-200"
                                />
                            </Form.Item>
                        </>
                    )}

                    <Button
                        htmlType="submit"
                        className={`!text-white !rounded-lg !w-full !h-10 !font-medium !mt-4 ${
                            constantType === 'YOUTUBE_LINK_CONSTANT_ONE'
                                ? '!bg-red-500 hover:!bg-red-600'
                                : '!bg-yellow-500 hover:!bg-yellow-600'
                        }`}
                    >
                        {editingConstant ? 'Update Configuration' : 'Create Configuration'}
                    </Button>
                </Form>
            </Modal>
        </div>
    );
};

export default ConstantsManager;